/**
 * NOS-5: Section context panel with element-level controls.
 * NOS-7: Background image upload, alt text, overlay opacity slider.
 * NOS-91: Add element catalog.
 * NOS-93: Remove section with confirmation (Section 8, Rule #5: hidden for Hero/Footer).
 * All visual values from docs/specs/BUILDER_DESIGN_SPEC.md Sections 8, 9, 10, 11.
 */
import { useState, useRef, useEffect } from "react"
import { toast } from "sonner"
import type { PageSection } from "@/templates/types"
import type { BlockType } from "./blocks/types"
import { BLOCK_VARIANTS, getSectionVariant } from "@/components/blocks/render/variants"
import { useCustomizationStore } from "@/stores/customizationStore"
import { useRewriteSection } from "@/hooks/useRewriteSection"
import { supabase } from "@/lib/supabase"
import { uploadVersionedOrgImage } from "@/lib/uploadOrgImage"
import { IconBtn } from "./IconBtn"
import { TinyInput } from "./TinyInput"
import { AddElementCatalog } from "./AddElementCatalog"
import { StockImageDialog } from "./StockImageDialog"
import { Loader2 } from "lucide-react"
import { RichTextInput } from "./RichTextInput"
import { SECTION_LABELS } from "./sectionLabels"
import { PANEL_CHROME } from "./ui/FloatingPanel"
import { DEFAULT_FOOTER_COLUMN_LAYOUT } from "@/lib/footerContent"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { BlockStyleOverrides } from "./blocks/types"

export interface SectionContextPanelProps {
  section: PageSection
  primaryColor: string
  onClose: () => void
  /** Optional overrides for non-store usage (e.g. blog blocks) */
  onUpdateSection?: (id: string, content: Record<string, unknown>) => void
  onRemoveSection?: (id: string) => void
  /** Panel width in px. Default 340. Pass larger value for blog mode. */
  width?: number
  /** Override the panel title (e.g. show "Content" instead of "Heading" in blog context). */
  titleOverride?: string
  /** Render as a full-width inline block (no floating chrome) for Outline mode. */
  inline?: boolean
  /** NOS-236: whether the edited page is the homepage (hero scroll-arrow default). */
  isHomePage?: boolean
}

/** Element types for display/editing - maps to Section 16 defaults */
type ElementType =
  | "heading"
  | "subheading"
  | "paragraph"
  | "button"
  | "image"
  | "stat"
  | "team-member"
  | "card"
  | "donation-selector"
  | "logo"
  | "social-icons"

interface PanelElement {
  id: string
  type: ElementType
  visible: boolean
  label: string
  /** Path to update in section content, e.g. "headline", "stats.0.value" */
  contentKey: string
  getValue: (content: Record<string, unknown>) => string | { value: string; label: string; bio?: string; icon?: string }
  setValue: (content: Record<string, unknown>, value: string | { value: string; label: string; bio?: string; icon?: string }) => Record<string, unknown>
  /** For array items (stats, members) - can reorder */
  arrayIndex?: number
  arrayKey?: string
}

/** Per-element-type style capabilities — only show controls that actually work */
const ELEMENT_STYLE_CAPS: Record<ElementType, {
  background?: true; text_color?: true; text_align?: true; corner_radius?: true; padding?: true
}> = {
  heading:             { text_align: true, text_color: true },
  subheading:          { text_align: true, text_color: true },
  paragraph:           { text_align: true, text_color: true },
  button:              { background: true, text_color: true, corner_radius: true },
  image:               { corner_radius: true },
  stat:                { background: true, text_color: true, corner_radius: true, padding: true, text_align: true },
  "team-member":       { background: true, text_color: true, corner_radius: true, padding: true },
  card:                { background: true, text_color: true, corner_radius: true, padding: true, text_align: true },
  "donation-selector": {},
  logo:                {},
  "social-icons":      {},
}

/** Whether section supports column layout (Section 5, 8) */
const COLUMN_OPTIONS: Record<string, { options: number[]; default: number }> = {
  cards:        { options: [2, 3],          default: 2 },
  impact_stats: { options: [2, 3, 4],       default: 2 },
  team_members: { options: [3, 4, 5, 6],    default: 3 },
}

/** Derive panel elements from section content */
function getSectionElements(section: PageSection): PanelElement[] {
  const content = (section.content as Record<string, unknown>) ?? {}
  const type = section.section_type
  const elements: PanelElement[] = []

  const add = (
    elType: ElementType,
    label: string,
    contentKey: string,
    getValue: (c: Record<string, unknown>) => string | { value: string; label: string; bio?: string; icon?: string },
    setValue: (c: Record<string, unknown>, v: string | { value: string; label: string; bio?: string; icon?: string }) => Record<string, unknown>,
    opts?: { arrayIndex?: number; arrayKey?: string }
  ) => {
    elements.push({
      id: opts?.arrayIndex !== undefined ? `${contentKey}-${opts.arrayIndex}` : contentKey,
      type: elType,
      visible: true,
      label,
      contentKey,
      getValue,
      setValue,
      arrayIndex: opts?.arrayIndex,
      arrayKey: opts?.arrayKey,
    })
  }

  /** Bug 9: Filter out scalar elements marked as deleted */
  const filterDeleted = (els: PanelElement[]) =>
    els.filter((el) => {
      if (el.arrayKey) return true
      const deleted = content[`${el.contentKey}_deleted` as keyof typeof content] as boolean | undefined
      return !deleted
    })

  /** Sort elements by element_order. element_order stores group keys (arrayKey ?? contentKey),
   *  so entire arrays move as a unit relative to scalars. Within an array, arrayIndex is preserved. */
  const sortByOrder = (els: PanelElement[]) => {
    const uniqueGroupKeys = [...new Set(els.map((e) => e.arrayKey ?? e.contentKey))]
    const order = (content.element_order as string[] | undefined) ?? uniqueGroupKeys
    return [...els].sort((a, b) => {
      const aGroup = a.arrayKey ?? a.contentKey
      const bGroup = b.arrayKey ?? b.contentKey
      const ia = order.indexOf(aGroup)
      const ib = order.indexOf(bGroup)
      if (ia === ib) return (a.arrayIndex ?? 0) - (b.arrayIndex ?? 0)
      if (ia === -1 && ib === -1) return 0
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    })
  }

  switch (type) {
    case "hero":
      add(
        "logo",
        "Logo",
        "logo",
        () => "",
        (c) => c
      )
      add(
        "heading",
        "Headline",
        "headline",
        (c) => (c.headline as string) ?? "",
        (c, v) => ({ ...c, headline: v as string })
      )
      add(
        "subheading",
        "Subheadline",
        "subheadline",
        (c) => (c.subheadline as string) ?? "",
        (c, v) => ({ ...c, subheadline: v as string })
      )
      add(
        "button",
        "CTA Button",
        "cta_text",
        (c) => (c.cta_text as string) ?? "",
        (c, v) => ({ ...c, cta_text: v as string })
      )
      break
    case "image_text":
      add(
        "heading",
        "Heading",
        "heading",
        (c) => (c.heading as string) ?? "",
        (c, v) => ({ ...c, heading: v as string })
      )
      add(
        "paragraph",
        "Body",
        "body",
        (c) => (c.body as string) ?? "",
        (c, v) => ({ ...c, body: v as string })
      )
      add(
        "image",
        "Image",
        "image_url",
        (c) => (c.image_url as string) ?? "",
        (c, v) => ({ ...c, image_url: v as string })
      )
      break
    case "impact_stats": {
      add(
        "heading",
        "Heading",
        "heading",
        (c) => (c.heading as string) ?? "",
        (c, v) => ({ ...c, heading: v as string })
      )
      const stats = (content.stats as Array<{ value: string; label: string; icon?: string }>) ?? []
      stats.forEach((_, i) => {
        add(
          "stat",
          `Stat ${i + 1}`,
          `stats.${i}`,
          (c) => {
            const s = ((c.stats as unknown[]) ?? [])[i] as { value?: string; label?: string; icon?: string }
            return { value: s?.value ?? "", label: s?.label ?? "", icon: s?.icon ?? "" }
          },
          (c, v) => {
            const val = v as { value: string; label: string; icon?: string }
            const arr = [...((c.stats as unknown[]) ?? [])]
            while (arr.length <= i) arr.push({ value: "", label: "", icon: "" })
            arr[i] = val
            return { ...c, stats: arr }
          },
          { arrayIndex: i, arrayKey: "stats" }
        )
      })
      break
    }
    case "donation_cta":
      add(
        "heading",
        "Heading",
        "heading",
        (c) => (c.heading as string) ?? "",
        (c, v) => ({ ...c, heading: v as string })
      )
      add(
        "paragraph",
        "Body",
        "body",
        (c) => (c.body as string) ?? "",
        (c, v) => ({ ...c, body: v as string })
      )
      add(
        "donation-selector",
        "Donation Amounts",
        "amounts",
        () => "Amounts: $25, $50, $100, Other",
        () => ({} as Record<string, unknown>)
      )
      add(
        "button",
        "CTA Text",
        "cta_text",
        (c) => (c.cta_text as string) ?? "",
        (c, v) => ({ ...c, cta_text: v as string })
      )
      // NOS-236: optional secondary CTA (renders beside Donate when set)
      add(
        "button",
        "Secondary Button (optional)",
        "secondary_cta_text",
        (c) => (c.secondary_cta_text as string) ?? "",
        (c, v) => ({ ...c, secondary_cta_text: v as string })
      )
      add(
        "button",
        "Secondary Button Link",
        "secondary_cta_url",
        (c) => (c.secondary_cta_url as string) ?? "",
        (c, v) => ({ ...c, secondary_cta_url: v as string })
      )
      break
    // NOS-162: embedded donation form — tiers edited as value/label pairs
    case "donation_form": {
      add(
        "heading",
        "Heading",
        "heading",
        (c) => (c.heading as string) ?? "",
        (c, v) => ({ ...c, heading: v as string })
      )
      add(
        "paragraph",
        "Body",
        "body",
        (c) => (c.body as string) ?? "",
        (c, v) => ({ ...c, body: v as string })
      )
      const tiers = (content.tiers as Array<{ amount?: number; impact_label?: string }>) ?? []
      tiers.forEach((_, i) => {
        add(
          "stat",
          `Tier ${i + 1} ($ / impact)`,
          `tiers.${i}`,
          (c) => {
            const t = ((c.tiers as unknown[]) ?? [])[i] as { amount?: number; impact_label?: string }
            return { value: String(t?.amount ?? ""), label: t?.impact_label ?? "", icon: "" }
          },
          (c, v) => {
            const val = v as { value: string; label: string }
            const arr = [...((c.tiers as unknown[]) ?? [])] as Array<{ amount?: number; impact_label?: string }>
            while (arr.length <= i) arr.push({})
            const amount = Number.parseFloat(val.value.replace(/[^0-9.]/g, ""))
            arr[i] = { amount: Number.isFinite(amount) ? amount : undefined, impact_label: val.label }
            return { ...c, tiers: arr }
          },
          { arrayIndex: i, arrayKey: "tiers" }
        )
      })
      add(
        "subheading",
        "Trust line",
        "trust_text",
        (c) => (c.trust_text as string) ?? "",
        (c, v) => ({ ...c, trust_text: v as string })
      )
      add(
        "button",
        "Button Text",
        "cta_text",
        (c) => (c.cta_text as string) ?? "",
        (c, v) => ({ ...c, cta_text: v as string })
      )
      break
    }
    // NOS-162: campaign banner
    case "campaign_banner":
      add(
        "heading",
        "Message",
        "message",
        (c) => (c.message as string) ?? "",
        (c, v) => ({ ...c, message: v as string })
      )
      add(
        "subheading",
        "Deadline (yyyy-mm-dd)",
        "deadline",
        (c) => (c.deadline as string) ?? "",
        (c, v) => ({ ...c, deadline: v as string })
      )
      add(
        "stat",
        "Goal ($ raised / $ goal)",
        "goal",
        (c) => ({
          value: String((c.raised_amount as number) ?? ""),
          label: String((c.goal_amount as number) ?? ""),
          icon: "",
        }),
        (c, v) => {
          const val = v as { value: string; label: string }
          const raised = Number.parseFloat(val.value.replace(/[^0-9.]/g, ""))
          const goal = Number.parseFloat(val.label.replace(/[^0-9.]/g, ""))
          return {
            ...c,
            raised_amount: Number.isFinite(raised) ? raised : undefined,
            goal_amount: Number.isFinite(goal) ? goal : undefined,
          }
        }
      )
      add(
        "button",
        "CTA Text",
        "cta_text",
        (c) => (c.cta_text as string) ?? "",
        (c, v) => ({ ...c, cta_text: v as string })
      )
      add(
        "subheading",
        "CTA Link",
        "cta_url",
        (c) => (c.cta_url as string) ?? "",
        (c, v) => ({ ...c, cta_url: v as string })
      )
      break
    case "cards": {
      if (content.heading) {
        add(
          "heading",
          "Heading",
          "heading",
          (c) => (c.heading as string) ?? "",
          (c, v) => ({ ...c, heading: v as string })
        )
      }
      if (content.subheading) {
        add(
          "subheading",
          "Subheading",
          "subheading",
          (c) => (c.subheading as string) ?? "",
          (c, v) => ({ ...c, subheading: v as string })
        )
      }
      const cardItems = (content.items as Array<{ title?: string; body?: string; image_url?: string }>) ?? []
      cardItems.forEach((item, i) => {
        add(
          "card",
          item.title || `Card ${i + 1}`,
          `items.${i}`,
          (c) => {
            const it = ((c.items as unknown[]) ?? [])[i] as { title?: string; body?: string; image_url?: string }
            return { value: it?.title ?? "", label: it?.body ?? "", icon: it?.image_url ?? "" }
          },
          (c, v) => {
            const val = v as { value: string; label: string; icon?: string }
            const arr = [...((c.items as unknown[]) ?? [])] as Record<string, unknown>[]
            while (arr.length <= i) arr.push({})
            arr[i] = { ...arr[i], title: val.value, body: val.label, image_url: val.icon }
            return { ...c, items: arr }
          },
          { arrayIndex: i, arrayKey: "items" }
        )
      })
      break
    }
    case "footer": {
      add(
        "paragraph",
        "Tagline",
        "tagline",
        (c) => (c.tagline as string) ?? "",
        (c, v) => ({ ...c, tagline: v as string })
      )
      // NOS-234: contact fields render in the footer but had no editing surface
      // (bench audit: "address shows on the site but isn't findable in the
      // footer panel"). Each is an element; clearing one removes it.
      const contactField = (label: string, field: "email" | "phone" | "address") =>
        add(
          "paragraph",
          label,
          `contact.${field}`,
          (c) => ((c.contact as Record<string, unknown> | undefined)?.[field] as string) ?? "",
          (c, v) => ({
            ...c,
            contact: { ...((c.contact as Record<string, unknown>) ?? {}), [field]: v as string },
          })
        )
      contactField("Contact email", "email")
      contactField("Contact phone", "phone")
      contactField("Address", "address")
      break
    }
    // NOS-219: dynamic latest-posts block — posts are fetched live, so only
    // the heading and View All label are inline-editable elements. Count,
    // images, and View All placement live in the "Latest posts" settings group.
    case "latest_posts":
      add(
        "heading",
        "Heading",
        "heading",
        (c) => (c.heading as string) ?? "",
        (c, v) => ({ ...c, heading: v as string })
      )
      add(
        "button",
        "View All label",
        "view_all_text",
        (c) => (c.view_all_text as string) ?? "",
        (c, v) => ({ ...c, view_all_text: v as string })
      )
      break
    case "paragraph":
      add(
        "paragraph",
        "Text",
        "text",
        (c) => {
          if (c.text && typeof c.text === "string") return c.text
          // Fallback: extract plain text from TipTap JSON stored in c.content
          const doc = c.content as { content?: Array<{ content?: Array<{ text?: string }> }> } | null | undefined
          if (doc && typeof doc === "object" && Array.isArray(doc.content)) {
            return doc.content
              .map((p) => (p.content ?? []).map((n) => n.text ?? "").join(""))
              .join("\n")
              .trim()
          }
          return ""
        },
        (c, v) => ({ ...c, text: v as string })
      )
      break
    default:
      elements.push({
        id: "default",
        type: "paragraph",
        visible: true,
        label: `${type} — configure in properties`,
        contentKey: "content",
        getValue: () => "",
        setValue: (c) => c,
      })
  }

  // Append elements added via the Add Element catalog (stored in content.blocks)
  const blocks = (content.blocks as Array<{ id: string; type: string; content: Record<string, unknown> }> | undefined) ?? []
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    const elType: ElementType =
      block.type === "heading" ? "heading" :
      block.type === "subheading" ? "subheading" :
      block.type === "button" || block.type === "link" ? "button" :
      block.type === "image" ? "image" :
      "paragraph"
    const blockLabel =
      block.type === "heading" ? "Heading" :
      block.type === "subheading" ? "Subheading" :
      block.type === "paragraph" ? "Paragraph" :
      block.type === "button" ? "Button" :
      block.type === "link" ? "Link" :
      block.type === "image" ? "Image" :
      block.type.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    const idx = i
    elements.push({
      id: block.id,
      type: elType,
      visible: (block as Record<string, unknown>).visible !== false,
      label: blockLabel,
      contentKey: `blocks.${idx}`,
      getValue: (c) => {
        const b = (c.blocks as typeof blocks)?.[idx]
        if (elType === "image") return (b?.content?.src as string) ?? ""
        return (b?.content?.text as string) ?? ""
      },
      setValue: (c, v) => {
        const arr = [...((c.blocks as typeof blocks) ?? [])]
        const field = elType === "image" ? "src" : "text"
        arr[idx] = { ...arr[idx], content: { ...arr[idx].content, [field]: v as string } }
        return { ...c, blocks: arr }
      },
      arrayIndex: idx,
      arrayKey: "blocks",
    })
  }

  return sortByOrder(filterDeleted(elements))
}

/** Element type icon (Section 8/9) */
function getElementIcon(type: ElementType): string {
  const icons: Record<ElementType, string> = {
    heading: "H",
    subheading: "h",
    paragraph: "¶",
    button: "◉",
    image: "🖼",
    stat: "#",
    "team-member": "👤",
    "card": "▪",
    "donation-selector": "$",
    logo: "◎",
    "social-icons": "⊕",
  }
  return icons[type] ?? "•"
}

/** NOS-154: labeled segmented-button row for the Spacing & alignment group. */
function StyleSegmentRow({
  label,
  options,
  activeId,
  primaryColor,
  onSelect,
}: {
  label: string
  options: Array<{ id: string; label: string }>
  activeId: string | undefined
  primaryColor: string
  onSelect: (id: string) => void
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 600,
          color: "rgba(255,255,255,0.3)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {options.map((opt) => {
          const active = opt.id === activeId
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onSelect(opt.id)}
              aria-pressed={active}
              aria-label={`${label}: ${opt.label}`}
              style={{
                flex: 1,
                padding: "7px 0",
                borderRadius: 7,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                border: active ? "none" : "1px solid rgba(255,255,255,0.08)",
                background: active ? primaryColor : "rgba(255,255,255,0.04)",
                color: active ? "#fff" : "rgba(255,255,255,0.5)",
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function SectionContextPanel({
  section,
  primaryColor,
  onClose,
  onUpdateSection: onUpdateSectionProp,
  onRemoveSection: onRemoveSectionProp,
  width = 340,
  titleOverride,
  inline = false,
  isHomePage = false,
}: SectionContextPanelProps) {
  const { updateSection: storeUpdateSection, removeSection: storeRemoveSection, markSectionReviewed, org, pushUndoHistory } = useCustomizationStore()
  const updateSection = onUpdateSectionProp ?? storeUpdateSection
  const removeSection = onRemoveSectionProp ?? storeRemoveSection
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadingCardIndex, setUploadingCardIndex] = useState<number | null>(null)
  const [showAddElementCatalog, setShowAddElementCatalog] = useState(false)
  const [rewriteInstruction, setRewriteInstruction] = useState("")
  const [openElStyles, setOpenElStyles] = useState<Record<string, boolean>>({})
  // Drag-to-reorder state for element rows
  const [draggingElIndex, setDraggingElIndex] = useState<number | null>(null)
  const [dropElTargetIndex, setDropElTargetIndex] = useState<number | null>(null)
  const dragElIndexRef = useRef<number | null>(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameValue, setNameValue] = useState("")
  const bgInputRef = useRef<HTMLInputElement>(null)
  // NOS-159: stock photo picker — one dialog, target set at open time.
  const [stockOpen, setStockOpen] = useState(false)
  const stockOnSelect = useRef<(url: string, alt: string) => void | Promise<void>>(() => {})
  const openStockPicker = (onSelect: (url: string, alt: string) => void | Promise<void>) => {
    stockOnSelect.current = onSelect
    setStockOpen(true)
  }
  const nameInputRef = useRef<HTMLInputElement>(null)
  const {
    rewrite,
    isLoading: isRewriting,
    error: rewriteError,
    undo,
    canUndo,
  } = useRewriteSection(section.id, org?.id ?? null)

  useEffect(() => {
    if (rewriteError) {
      toast.error(rewriteError)
    }
  }, [rewriteError])


  const content = (section.content as Record<string, unknown>) ?? {}
  const importSource = content.import_source as string | undefined
  const isUnreviewed = importSource === "import" || importSource === "ai_generated"

  const handleApprove = async () => {
    await markSectionReviewed(section.id)
    toast.success("Section approved")
  }

  const customName = content._name as string | undefined
  const sectionLabel = titleOverride ?? customName ?? SECTION_LABELS[section.section_type] ?? section.section_type

  const handleNameClick = () => {
    if (titleOverride) return
    setNameValue(customName ?? SECTION_LABELS[section.section_type] ?? section.section_type)
    setIsEditingName(true)
    setTimeout(() => nameInputRef.current?.select(), 0)
  }

  const handleNameSave = () => {
    setIsEditingName(false)
    const trimmed = nameValue.trim()
    const defaultLabel = SECTION_LABELS[section.section_type] ?? section.section_type
    // If user cleared it or typed the default, remove custom name
    const newName = trimmed === "" || trimmed === defaultLabel ? undefined : trimmed
    updateSection(section.id, { ...content, _name: newName })
  }

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleNameSave()
    if (e.key === "Escape") setIsEditingName(false)
  }
  const elements = getSectionElements(section)
  const columnConfig = COLUMN_OPTIONS[section.section_type]
  const isColumnable = !!columnConfig
  const columns = (content.columns as number) ?? columnConfig?.default ?? 1
  /** NOS-152: layout variants for this block type (undefined = block has no variants) */
  const variantDefs = BLOCK_VARIANTS[section.section_type as BlockType]
  const currentVariantId = getSectionVariant(content) ?? variantDefs?.[0]?.id
  /** Spec Section 20 Rule #5: Remove Section button HIDDEN for Hero and Footer */
  const hideRemove = section.section_type === "hero" || section.section_type === "footer"

  // Footer-specific: quick links management (NavContextPanel-style)
  type QuickLink = { label: string; href: string; open_in_new_tab?: boolean }
  const quickLinks = (content.quick_links as QuickLink[] | undefined) ?? []
  const [qlLocalItems, setQlLocalItems] = useState<QuickLink[]>(quickLinks)
  useEffect(() => { setQlLocalItems(quickLinks) }, [section.id, JSON.stringify(quickLinks)])  // eslint-disable-line react-hooks/exhaustive-deps
  const [qlLabelDrafts, setQlLabelDrafts] = useState<Record<number, string>>({})
  const [qlHrefDrafts, setQlHrefDrafts] = useState<Record<number, string>>({})
  const [qlDraggingIndex, setQlDraggingIndex] = useState<number | null>(null)
  const [qlDropTargetIndex, setQlDropTargetIndex] = useState<number | null>(null)
  const qlDragIndexRef = useRef<number | null>(null)
  const [addingQuickLink, setAddingQuickLink] = useState(false)
  const [newQLLabel, setNewQLLabel] = useState("")
  const [newQLHref, setNewQLHref] = useState("")
  const [newQLNewTab, setNewQLNewTab] = useState(false)

  const saveQuickLinks = (next: QuickLink[]) => {
    updateSection(section.id, { ...content, quick_links: next })
  }

  const handleAddQuickLink = () => {
    if (!newQLLabel.trim() || !newQLHref.trim()) return
    const href = newQLHref.startsWith("/") || newQLHref.startsWith("http") ? newQLHref.trim() : `/${newQLHref.trim()}`
    saveQuickLinks([...quickLinks, { label: newQLLabel.trim(), href, open_in_new_tab: newQLNewTab }])
    setNewQLLabel("")
    setNewQLHref("")
    setNewQLNewTab(false)
    setAddingQuickLink(false)
  }

  const handleRemoveQuickLink = (index: number) => {
    saveQuickLinks(quickLinks.filter((_, i) => i !== index))
  }

  const handleQlLabelBlur = (index: number) => {
    const label = qlLabelDrafts[index] ?? quickLinks[index]?.label
    if (label === quickLinks[index]?.label) return
    const next = quickLinks.map((l, i) => i === index ? { ...l, label } : l)
    saveQuickLinks(next)
  }

  const handleQlHrefBlur = (index: number) => {
    const raw = qlHrefDrafts[index] ?? quickLinks[index]?.href
    const href = raw.startsWith("/") || raw.startsWith("http") ? raw : `/${raw}`
    if (href === quickLinks[index]?.href) return
    const next = quickLinks.map((l, i) => i === index ? { ...l, href } : l)
    saveQuickLinks(next)
  }

  const handleQlToggleNewTab = (index: number) => {
    const next = quickLinks.map((l, i) => i === index ? { ...l, open_in_new_tab: !l.open_in_new_tab } : l)
    saveQuickLinks(next)
  }

  const handleQlDragStart = (e: React.DragEvent, index: number) => {
    qlDragIndexRef.current = index
    setQlDraggingIndex(index)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", String(index))
  }
  const handleQlDragEnter = (e: React.DragEvent, index: number) => { e.preventDefault(); setQlDropTargetIndex(index) }
  const handleQlDragOver = (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = "move" }
  const handleQlDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    const fromIndex = qlDragIndexRef.current
    setQlDraggingIndex(null)
    setQlDropTargetIndex(null)
    qlDragIndexRef.current = null
    if (fromIndex === null || fromIndex === dropIndex) return
    const reordered = [...qlLocalItems]
    const [moved] = reordered.splice(fromIndex, 1)
    reordered.splice(dropIndex, 0, moved)
    setQlLocalItems(reordered)
    saveQuickLinks(reordered)
  }
  const handleQlDragEnd = () => { setQlDraggingIndex(null); setQlDropTargetIndex(null); qlDragIndexRef.current = null }

  // Footer-specific: social links management
  const SOCIAL_PLATFORMS = ["Facebook", "Twitter", "Instagram", "LinkedIn", "YouTube"] as const
  const socialLinks = (content.social_links as Array<{ label: string; url: string }> | undefined) ?? []
  const getSocialUrl = (platform: string) => socialLinks.find((l) => l.label === platform)?.url ?? ""
  const setSocialUrl = (platform: string, url: string) => {
    const next = socialLinks.filter((l) => l.label !== platform)
    if (url.trim()) next.push({ label: platform, url: url.trim() })
    updateSection(section.id, { ...content, social_links: next })
  }

  const handleColumnChange = (cols: number) => {
    updateSection(section.id, { ...content, columns: cols })
  }

  /** NOS-152: switch layout variant. Content fields are shared across variants, so nothing is lost. */
  const handleVariantChange = (variantId: string) => {
    if (variantId === currentVariantId) return
    pushUndoHistory(section.id, { ...content })
    updateSection(section.id, { ...content, variant: variantId })
  }

  /** NOS-154: per-section spacing/alignment/width overrides stored in content._styles */
  const sectionStyles = (content._styles as BlockStyleOverrides | undefined) ?? {}
  const currentPadding = sectionStyles.padding ?? "normal"
  const currentAlign = sectionStyles.text_align
  const currentWidth = sectionStyles.width === "contained" ? "contained" : "full"

  const handleSectionStyleChange = (key: keyof BlockStyleOverrides, value: string | undefined) => {
    pushUndoHistory(section.id, { ...content })
    const nextStyles: BlockStyleOverrides = { ...sectionStyles }
    if (value === undefined) {
      delete nextStyles[key]
    } else {
      ;(nextStyles as Record<string, unknown>)[key] = value
    }
    updateSection(section.id, { ...content, _styles: nextStyles })
  }

  const handleElementChange = (
    el: PanelElement,
    value: string | { value: string; label: string; bio?: string; icon?: string }
  ) => {
    const newContent = el.setValue(content, value)
    updateSection(section.id, newContent)
  }

  const handleElDragStart = (e: React.DragEvent, index: number) => {
    dragElIndexRef.current = index
    setDraggingElIndex(index)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", String(index))
  }
  const handleElDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDropElTargetIndex(index)
  }
  const handleElDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }
  const handleElDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    const fromIndex = dragElIndexRef.current
    setDraggingElIndex(null)
    setDropElTargetIndex(null)
    dragElIndexRef.current = null
    if (fromIndex === null || fromIndex === dropIndex) return
    const fromEl = elements[fromIndex]
    const toEl = elements[dropIndex]
    if (!fromEl || !toEl) return

    const fromGroup = fromEl.arrayKey ?? fromEl.contentKey
    const toGroup = toEl.arrayKey ?? toEl.contentKey

    // Same array → reorder within it
    if (fromEl.arrayKey && fromEl.arrayKey === toEl.arrayKey
        && fromEl.arrayIndex !== undefined && toEl.arrayIndex !== undefined) {
      const arr = [...((content[fromEl.arrayKey] as unknown[]) ?? [])]
      const [moved] = arr.splice(fromEl.arrayIndex, 1)
      arr.splice(toEl.arrayIndex, 0, moved)
      updateSection(section.id, { ...content, [fromEl.arrayKey]: arr })
      return
    }

    // Different groups (scalar↔scalar, scalar↔array, array↔array) → reorder at group level
    if (fromGroup === toGroup) return
    const uniqueGroupKeys = [...new Set(elements.map((e) => e.arrayKey ?? e.contentKey))]
    const existingOrder = (content.element_order as string[] | undefined) ?? uniqueGroupKeys
    // Merge any new groups not yet in the stored order
    const fullOrder = [...new Set([...existingOrder, ...uniqueGroupKeys])]
    const newOrder = fullOrder.filter((k) => k !== fromGroup)
    const insertAt = newOrder.indexOf(toGroup)
    if (insertAt === -1) return
    newOrder.splice(insertAt, 0, fromGroup)
    updateSection(section.id, { ...content, element_order: newOrder })
  }
  const handleElDragEnd = () => {
    setDraggingElIndex(null)
    setDropElTargetIndex(null)
    dragElIndexRef.current = null
  }

  const handleToggleVisibility = (el: PanelElement) => {
    if (el.arrayKey && el.arrayIndex !== undefined) {
      const arr = (content[el.arrayKey] as Array<Record<string, unknown>>) ?? []
      const currentlyVisible = arr[el.arrayIndex]?.visible !== false
      if (currentlyVisible) pushUndoHistory(section.id, { ...content })
      const item = { ...arr[el.arrayIndex], visible: !currentlyVisible }
      const next = [...arr]
      next[el.arrayIndex] = item
      updateSection(section.id, { ...content, [el.arrayKey]: next })
    }
  }

  const handleDeleteElement = (el: PanelElement) => {
    pushUndoHistory(section.id, { ...content })
    if (el.arrayKey && el.arrayIndex !== undefined) {
      const arr = ((content[el.arrayKey] as unknown[]) ?? []).filter((_, i) => i !== el.arrayIndex)
      updateSection(section.id, { ...content, [el.arrayKey]: arr })
    } else {
      /* Bug 9: Scalar delete: set _deleted flag and clear value so row disappears from panel */
      const deletedKey = `${el.contentKey}_deleted` as keyof typeof content
      const clearVal =
        el.type === "paragraph" || el.type === "heading" || el.type === "subheading" || el.type === "button"
          ? ""
          : { value: "", label: "", icon: "", bio: "" }
      const newContent = el.setValue(content, clearVal) as Record<string, unknown>
      newContent[deletedKey] = true
      updateSection(section.id, newContent)
    }
  }

  const handleDuplicateElement = (el: PanelElement) => {
    if (!el.arrayKey || el.arrayIndex === undefined) return
    const arr = [...((content[el.arrayKey] as unknown[]) ?? [])]
    const copy = JSON.parse(JSON.stringify(arr[el.arrayIndex])) as unknown
    arr.splice(el.arrayIndex + 1, 0, copy)
    updateSection(section.id, { ...content, [el.arrayKey]: arr })
  }

  const getElStyle = (el: PanelElement): BlockStyleOverrides => {
    if (el.arrayKey && el.arrayIndex !== undefined) {
      const arr = (content[el.arrayKey] as Array<Record<string, unknown>>) ?? []
      return (arr[el.arrayIndex]?._styles as BlockStyleOverrides) ?? {}
    }
    const styles = (content._el_styles as Record<string, BlockStyleOverrides> | undefined) ?? {}
    return styles[el.contentKey] ?? {}
  }

  const updateElStyle = (el: PanelElement, next: Partial<BlockStyleOverrides>) => {
    if (el.arrayKey && el.arrayIndex !== undefined) {
      const arr = [...((content[el.arrayKey] as unknown[]) ?? [])] as Record<string, unknown>[]
      const current = (arr[el.arrayIndex]?._styles as BlockStyleOverrides) ?? {}
      arr[el.arrayIndex] = { ...arr[el.arrayIndex], _styles: { ...current, ...next } }
      updateSection(section.id, { ...content, [el.arrayKey]: arr })
      return
    }
    const styles = (content._el_styles as Record<string, BlockStyleOverrides> | undefined) ?? {}
    updateSection(section.id, {
      ...content,
      _el_styles: { ...styles, [el.contentKey]: { ...(styles[el.contentKey] ?? {}), ...next } },
    })
  }

  const handleToggleScalarVisibility = (el: PanelElement) => {
    if (el.arrayKey) return
    const hiddenKey = `${el.contentKey}_hidden` as keyof typeof content
    const current = content[hiddenKey] as boolean | undefined
    if (!current) pushUndoHistory(section.id, { ...content })
    updateSection(section.id, { ...content, [hiddenKey]: !current })
  }

  /** NOS-91: Append element to section content */
  const handleAddElement = (compType: string, defaultContent: Record<string, unknown>) => {
    const sectionType = section.section_type
    const next = { ...content } as Record<string, unknown>

    if (sectionType === "impact_stats" && compType === "stat") {
      const stats = ((next.stats as Array<{ value?: string; label?: string; icon?: string }>) ?? []).slice()
      stats.push({
        value: (defaultContent.value as string) ?? "0",
        label: (defaultContent.label as string) ?? "Label",
        icon: (defaultContent.icon as string) ?? "",
      })
      next.stats = stats
    } else if (sectionType === "team_members" && compType === "team-member") {
      const members = ((next.members as Array<{ name?: string; role?: string; bio?: string }>) ?? []).slice()
      members.push({
        name: (defaultContent.name as string) ?? "Name",
        role: (defaultContent.role as string) ?? "Role",
        bio: (defaultContent.bio as string) ?? "",
      })
      next.members = members
    } else if (sectionType === "testimonials" && compType === "testimonial") {
      const testimonials = ((next.testimonials as Array<{ quote?: string; author?: string }>) ?? []).slice()
      testimonials.push({
        quote: (defaultContent.quote as string) ?? "Testimonial text",
        author: (defaultContent.author as string) ?? "Author",
      })
      next.testimonials = testimonials
    } else {
      const blocks = ((next.blocks as Array<{ id: string; type: string; content: Record<string, unknown> }>) ?? []).slice()
      blocks.push({
        id: crypto.randomUUID(),
        type: compType,
        content: defaultContent,
      })
      next.blocks = blocks
    }
    updateSection(section.id, next)
    setShowAddElementCatalog(false)
    toast.success("Element added")
  }

  const isElementComplete = (el: PanelElement): boolean => {
    const val = el.getValue(content)
    if (typeof val === "string") return val.trim().length > 0
    return !!(val.value?.trim() || val.label?.trim())
  }

  const handleRemoveSection = async () => {
    try {
      await removeSection(section.id)
      setRemoveDialogOpen(false)
      onClose()
    } catch {
      toast.error("Failed to remove section")
    }
  }

  const handleRewriteAI = async () => {
    await rewrite(rewriteInstruction || undefined)
  }

  // NOS-7: Background image upload
  const IMAGE_ACCEPT = "image/png,image/jpeg,image/svg+xml"
  const IMAGE_MAX_BYTES = 2 * 1024 * 1024
  const backgroundImage = content.background_image as string | undefined
  const backgroundImageAlt = (content.background_image_alt as string) ?? ""
  const overlayOpacity = Math.min(
    1,
    Math.max(0, (content.background_overlay_opacity as number) ?? 0.55)
  )
  const altWarning = backgroundImage && !backgroundImageAlt.trim()

  const handleBgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !org?.id) return
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Image must be 2MB or smaller")
      return
    }
    const allowed = IMAGE_ACCEPT.split(",").map((m) => m.trim())
    if (!allowed.some((m) => file.type === m)) {
      toast.error("Image must be PNG, JPEG, or SVG")
      return
    }
    setUploading(true)
    const { data: uploaded, error: uploadError } = await uploadVersionedOrgImage(
      supabase,
      "logos",
      `${org.id}/section-bg/${section.id}/bg`,
      file
    )
    if (uploadError || !uploaded) {
      setUploading(false)
      toast.error(uploadError?.message ?? "Upload failed")
      return
    }
    await updateSection(section.id, { ...content, background_image: uploaded.publicUrl })
    setUploading(false)
    toast.success("Background image uploaded")
    e.target.value = ""
  }

  const handleRemoveBgImage = async () => {
    await updateSection(section.id, {
      ...content,
      background_image: undefined,
      background_image_alt: undefined,
    })
    toast.success("Background image removed")
  }

  // Footer-specific logo (light variant for the dark footer). Stored on the footer
  // section content, which customizationStore mirrors into org_customizations.footer_content.
  const footerLogoUrl = (content.logo_url as string | undefined) ?? ""
  // NOS-217: footer background color (imported from the source site or set here).
  // Stored on footer_content.background; empty = default dark (#0f0f14).
  const footerBackground = (content.background as string | undefined) ?? ""
  const footerBackgroundOrDefault = /^#[0-9a-f]{6}$/i.test(footerBackground)
    ? footerBackground
    : "#0f0f14"
  const handleFooterBackgroundChange = async (value: string) => {
    await updateSection(section.id, { ...content, background: value })
  }
  const handleClearFooterBackground = async () => {
    await updateSection(section.id, { ...content, background: undefined })
  }
  // Logo style: how the org logo stays visible on a colliding footer background.
  // "knockout" (default) = solid white/black silhouette; "chip" = full-color on a chip.
  const footerLogoTreatment = content.logo_treatment === "chip" ? "chip" : "knockout"
  const handleFooterLogoTreatment = async (value: "knockout" | "chip") => {
    await updateSection(section.id, {
      ...content,
      logo_treatment: value === "knockout" ? undefined : value,
    })
  }
  const [uploadingFooterLogo, setUploadingFooterLogo] = useState(false)
  const handleFooterLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !org?.id) return
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Image must be 2MB or smaller")
      return
    }
    const allowed = IMAGE_ACCEPT.split(",").map((m) => m.trim())
    if (!allowed.some((m) => file.type === m)) {
      toast.error("Image must be PNG, JPEG, or SVG")
      return
    }
    setUploadingFooterLogo(true)
    const { data: uploaded, error: uploadError } = await uploadVersionedOrgImage(
      supabase,
      "logos",
      `${org.id}/footer/logo`,
      file
    )
    if (uploadError || !uploaded) {
      setUploadingFooterLogo(false)
      toast.error(uploadError?.message ?? "Upload failed")
      return
    }
    // Versioned upload path — the URL itself changes, no ?v= cache-bust needed.
    await updateSection(section.id, { ...content, logo_url: uploaded.publicUrl })
    setUploadingFooterLogo(false)
    toast.success("Footer logo updated")
    e.target.value = ""
  }
  const handleRemoveFooterLogo = async () => {
    await updateSection(section.id, { ...content, logo_url: undefined })
    toast.success("Footer logo removed — using the main logo")
  }
  // NOS-234: which column each movable footer group renders in. Stored on
  // footer_content.column_layout; resolveFooterContent validates + defaults.
  const footerColumnLayout: Record<string, 1 | 2 | 3> = { ...DEFAULT_FOOTER_COLUMN_LAYOUT }
  const rawFooterLayout = content.column_layout
  if (rawFooterLayout && typeof rawFooterLayout === "object" && !Array.isArray(rawFooterLayout)) {
    for (const key of Object.keys(DEFAULT_FOOTER_COLUMN_LAYOUT)) {
      const v = (rawFooterLayout as Record<string, unknown>)[key]
      if (v === 1 || v === 2 || v === 3) footerColumnLayout[key] = v
    }
  }
  const handleFooterColumnChange = async (group: string, col: 1 | 2 | 3) => {
    await updateSection(section.id, {
      ...content,
      column_layout: { ...footerColumnLayout, [group]: col },
    })
  }

  return (
    <>
      {org?.id && (
        <StockImageDialog
          orgId={org.id}
          open={stockOpen}
          onClose={() => setStockOpen(false)}
          onSelect={(url, alt) => stockOnSelect.current(url, alt)}
        />
      )}
      <div
        className={inline ? undefined : "animate-slideIn"}
        style={inline ? {
          width: "100%",
          background: "rgba(12,12,16,0.97)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          flexDirection: "column",
        } : {
          ...PANEL_CHROME,
          top: 100,
          right: 16,
          bottom: 16,
          width,
          zIndex: 1000,
        }}
      >
        {/* Panel header - Section 8 */}
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: primaryColor,
                flexShrink: 0,
              }}
            />
            {isEditingName ? (
              <input
                ref={nameInputRef}
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={handleNameKeyDown}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  background: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 4,
                  padding: "2px 6px",
                  outline: "none",
                  width: "100%",
                }}
              />
            ) : (
              <span
                title={titleOverride ? undefined : "Click to rename"}
                onClick={handleNameClick}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  cursor: titleOverride ? "default" : "text",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {sectionLabel}
              </span>
            )}
          </div>
          {!inline && (
            <IconBtn size={26} onClick={onClose}>
              ✕
            </IconBtn>
          )}
        </div>

        {/* Unreviewed import banner */}
        {isUnreviewed && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "8px 14px",
              background: importSource === "import"
                ? "rgba(217,119,6,0.15)"
                : "rgba(124,58,237,0.15)",
              borderBottom: `1px solid ${importSource === "import" ? "rgba(217,119,6,0.25)" : "rgba(124,58,237,0.25)"}`,
            }}
          >
            <span style={{ fontSize: 11, color: importSource === "import" ? "#fbbf24" : "#a78bfa", fontWeight: 500 }}>
              {importSource === "import" ? "Imported — not reviewed" : "AI generated — not reviewed"}
            </span>
            <button
              type="button"
              onClick={() => void handleApprove()}
              style={{
                flexShrink: 0,
                padding: "4px 10px",
                background: importSource === "import"
                  ? "rgba(217,119,6,0.3)"
                  : "rgba(124,58,237,0.3)",
                border: `1px solid ${importSource === "import" ? "rgba(217,119,6,0.5)" : "rgba(124,58,237,0.5)"}`,
                borderRadius: 6,
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Approve
            </button>
          </div>
        )}

        {/* Panel body - scrollable */}
        <div
          style={{
            ...(inline ? {} : { flex: 1, overflowY: "auto" as const }),
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Layout variant picker - NOS-152 */}
          {variantDefs && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: 8,
                }}
              >
                Layout
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {variantDefs.map((v) => {
                  const active = v.id === currentVariantId
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => handleVariantChange(v.id)}
                      aria-pressed={active}
                      aria-label={`${v.label} layout`}
                      style={{
                        padding: 0,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          height: 58,
                          borderRadius: 6,
                          overflow: "hidden",
                          background: "#26262e",
                          border: active ? `2px solid ${primaryColor}` : "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <v.Thumb />
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          marginTop: 4,
                          color: active ? "#fff" : "rgba(255,255,255,0.5)",
                        }}
                      >
                        {v.label}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* NOS-236: hero height + per-page scroll arrow */}
          {section.section_type === "hero" && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: 8,
                }}
              >
                Hero
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <StyleSegmentRow
                  label="Height"
                  options={[
                    { id: "full", label: "Full" },
                    { id: "70", label: "70%" },
                    { id: "50", label: "50%" },
                  ]}
                  activeId={(content.hero_height as string) ?? "full"}
                  primaryColor={primaryColor}
                  onSelect={(id) => {
                    pushUndoHistory(section.id, { ...content })
                    updateSection(section.id, { ...content, hero_height: id })
                  }}
                />
                {(currentVariantId ?? "centered") === "centered" && (
                  <StyleSegmentRow
                    label="Scroll arrow"
                    options={[
                      { id: "on", label: "On" },
                      { id: "off", label: "Off" },
                    ]}
                    activeId={
                      typeof content.scroll_arrow === "boolean"
                        ? content.scroll_arrow
                          ? "on"
                          : "off"
                        : isHomePage
                          ? "on"
                          : "off"
                    }
                    primaryColor={primaryColor}
                    onSelect={(id) => {
                      pushUndoHistory(section.id, { ...content })
                      updateSection(section.id, { ...content, scroll_arrow: id === "on" })
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* NOS-236: image_text image side toggle */}
          {section.section_type === "image_text" && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: 8,
                }}
              >
                Image
              </div>
              <StyleSegmentRow
                label="Image side"
                options={[
                  { id: "left", label: "Left" },
                  { id: "right", label: "Right" },
                ]}
                activeId={(content.image_position as string) ?? "left"}
                primaryColor={primaryColor}
                onSelect={(id) => {
                  pushUndoHistory(section.id, { ...content })
                  updateSection(section.id, { ...content, image_position: id })
                }}
              />
            </div>
          )}

          {/* Spacing & alignment - NOS-154 */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "rgba(255,255,255,0.35)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                marginBottom: 8,
              }}
            >
              Spacing &amp; alignment
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <StyleSegmentRow
                label="Vertical padding"
                options={[
                  { id: "compact", label: "Compact" },
                  { id: "normal", label: "Normal" },
                  { id: "spacious", label: "Spacious" },
                ]}
                activeId={currentPadding}
                primaryColor={primaryColor}
                onSelect={(id) => handleSectionStyleChange("padding", id)}
              />
              <StyleSegmentRow
                label="Alignment"
                options={[
                  { id: "left", label: "Left" },
                  { id: "center", label: "Center" },
                ]}
                activeId={currentAlign}
                primaryColor={primaryColor}
                onSelect={(id) => handleSectionStyleChange("text_align", id)}
              />
              <StyleSegmentRow
                label="Width"
                options={[
                  { id: "full", label: "Full" },
                  { id: "contained", label: "Contained" },
                ]}
                activeId={currentWidth}
                primaryColor={primaryColor}
                onSelect={(id) => handleSectionStyleChange("width", id === "contained" ? "contained" : undefined)}
              />
            </div>
          </div>

          {/* Column layout control - Section 8 */}
          {isColumnable && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: 8,
                }}
              >
                Column layout
              </div>
              <div style={{ display: "flex", gap: 4 }}>
              {(columnConfig?.options ?? []).map((cols) => (
                <button
                  key={cols}
                  type="button"
                  onClick={() => handleColumnChange(cols)}
                  style={{
                    flex: 1,
                    padding: "7px 0",
                    borderRadius: 7,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: columns === cols ? "none" : "1px solid rgba(255,255,255,0.08)",
                    background: columns === cols ? primaryColor : "rgba(255,255,255,0.04)",
                    color: columns === cols ? "#fff" : "rgba(255,255,255,0.5)",
                  }}
                >
                  {cols} col{cols > 1 ? "s" : ""}
                </button>
              ))}
              </div>
            </div>
          )}

          {/* NOS-219: latest-posts block settings — count, images, View All placement */}
          {section.section_type === "latest_posts" && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: 8,
                }}
              >
                Latest posts
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <StyleSegmentRow
                  label="Posts shown"
                  options={[
                    { id: "2", label: "2" },
                    { id: "3", label: "3" },
                    { id: "4", label: "4" },
                    { id: "6", label: "6" },
                  ]}
                  activeId={String((content.count as number) ?? 3)}
                  primaryColor={primaryColor}
                  onSelect={(id) => updateSection(section.id, { ...content, count: Number(id) })}
                />
                <StyleSegmentRow
                  label="Post images"
                  options={[
                    { id: "show", label: "Show" },
                    { id: "hide", label: "Hide" },
                  ]}
                  activeId={((content.show_images as boolean | undefined) ?? true) ? "show" : "hide"}
                  primaryColor={primaryColor}
                  onSelect={(id) => updateSection(section.id, { ...content, show_images: id === "show" })}
                />
                <StyleSegmentRow
                  label="View All link"
                  options={[
                    { id: "header", label: "Header" },
                    { id: "below", label: "Below" },
                    { id: "none", label: "Hidden" },
                  ]}
                  activeId={(content.view_all as string) ?? "header"}
                  primaryColor={primaryColor}
                  onSelect={(id) => updateSection(section.id, { ...content, view_all: id })}
                />
              </div>
            </div>
          )}

          {/* Background image zone - Section 8, NOS-7 upload. Dashed border per reference. */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "rgba(255,255,255,0.35)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                marginBottom: 8,
              }}
            >
              Background image
            </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => bgInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") bgInputRef.current?.click()
            }}
            style={{
              minHeight: 52,
              height: 52,
              borderRadius: 8,
              border: "1px dashed rgba(255,255,255,0.1)",
              background: backgroundImage
                ? `url(${backgroundImage}) center/cover`
                : "rgba(255,255,255,0.02)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              color: "rgba(255,255,255,0.35)",
              fontSize: 11,
              cursor: "pointer",
              transition: "all 0.12s",
            }}
          >
            <input
              ref={bgInputRef}
              type="file"
              accept={IMAGE_ACCEPT}
              onChange={handleBgUpload}
              style={{ display: "none" }}
              disabled={uploading}
            />
            {uploading ? (
              <span>Uploading…</span>
            ) : backgroundImage ? (
              <>
                <span>Change image</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleRemoveBgImage()
                  }}
                  style={{
                    padding: "4px 8px",
                    background: "rgba(239,68,68,0.2)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    borderRadius: 6,
                    color: "#ef4444",
                    fontSize: 10,
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </>
            ) : (
              <span>＋ Add background image</span>
            )}
          </div>
          {/* NOS-159: stock photo alternative to upload */}
          <button
            type="button"
            data-testid="stock-bg-btn"
            onClick={() =>
              openStockPicker((url) =>
                updateSection(section.id, { ...content, background_image: url })
              )
            }
            style={{
              marginTop: 6,
              padding: 0,
              border: "none",
              background: "transparent",
              color: "rgba(255,255,255,0.4)",
              fontSize: 11,
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            Browse stock photos
          </button>
          </div>
          {backgroundImage ? (
            <>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: altWarning ? "#f59e0b" : "rgba(255, 255, 255, 0.35)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  Alt text {altWarning && "(required for accessibility)"}
                </div>
                <TinyInput
                  placeholder="Describe the image for screen readers"
                  value={backgroundImageAlt}
                  onChange={(e) =>
                    updateSection(section.id, {
                      ...content,
                      background_image_alt: e.target.value,
                    })
                  }
                />
                {altWarning && (
                  <div style={{ marginTop: 6, fontSize: 10, color: "#f59e0b" }}>
                    Add alt text for accessibility
                  </div>
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "rgba(255, 255, 255, 0.35)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  Overlay opacity: {Math.round(overlayOpacity * 100)}%
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round(overlayOpacity * 100)}
                  onChange={(e) =>
                    updateSection(section.id, {
                      ...content,
                      background_overlay_opacity: Number(e.target.value) / 100,
                    })
                  }
                  step={5}
                  style={{ width: "100%", accentColor: primaryColor }}
                />
              </div>
            </>
          ) : null}

          {/* Element rows - Section 8 */}
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: "rgba(255,255,255,0.35)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              marginBottom: 8,
            }}
          >
            Elements
          </div>
          {elements.map((el, index) => {
            const val = el.getValue(content)
            const isVisible =
              el.arrayKey && el.arrayIndex !== undefined
                ? ((content[el.arrayKey] as Array<{ visible?: boolean }>)?.[el.arrayIndex]?.visible ?? true)
                : !(content[`${el.contentKey}_hidden` as keyof typeof content] as boolean | undefined)
            const isReadOnly = el.type === "donation-selector" || el.type === "social-icons" || el.type === "logo"

            const isDragging = draggingElIndex === index
            const isDropTarget = dropElTargetIndex === index && draggingElIndex !== index
            return (
              <div
                key={el.id}
                draggable
                onDragStart={(e) => handleElDragStart(e, index)}
                onDragEnter={(e) => handleElDragEnter(e, index)}
                onDragOver={handleElDragOver}
                onDrop={(e) => handleElDrop(e, index)}
                onDragEnd={handleElDragEnd}
                style={{
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: isDropTarget ? "rgba(255,255,255,0.08)" : isVisible ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.01)",
                  border: `1px solid ${isDropTarget ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.05)"}`,
                  opacity: isDragging ? 0.4 : isVisible ? 1 : 0.45,
                  transition: "background 0.1s, border-color 0.1s, opacity 0.1s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: isVisible && !isReadOnly ? 8 : 0,
                  }}
                >
                  {/* Drag handle */}
                  <span
                    style={{
                      cursor: "grab",
                      color: "rgba(255,255,255,0.25)",
                      fontSize: 12,
                      lineHeight: 1,
                      flexShrink: 0,
                      userSelect: "none",
                      width: 14,
                      textAlign: "center",
                    }}
                    title="Drag to reorder"
                  >
                    ⠿
                  </span>
                  <span
                    style={{
                      width: 16,
                      textAlign: "center",
                      fontSize: 11,
                      color: "rgba(255,255,255,0.3)",
                      flexShrink: 0,
                    }}
                  >
                    {getElementIcon(el.type)}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 11,
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    {el.label}
                  </span>
                  <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 4,
                        background: isElementComplete(el) ? "rgba(34,197,94,0.15)" : "transparent",
                        color: isElementComplete(el) ? "#22c55e" : "rgba(255,255,255,0.2)",
                        fontSize: 8,
                        fontWeight: 700,
                      }}
                      title={isElementComplete(el) ? "Complete" : "Add content"}
                    >
                      ✓
                    </span>
                    <IconBtn
                      size={22}
                      active={isVisible}
                      onClick={() =>
                        el.arrayKey ? handleToggleVisibility(el) : handleToggleScalarVisibility(el)
                      }
                    >
                      {isVisible ? "👁" : "—"}
                    </IconBtn>
                    {el.arrayKey !== undefined && section.section_type !== "accordion" && (
                      <IconBtn
                        size={22}
                        title="Duplicate"
                        onClick={() => handleDuplicateElement(el)}
                      >
                        ⧉
                      </IconBtn>
                    )}
                    <IconBtn
                      size={22}
                      danger
                      onClick={() => handleDeleteElement(el)}
                    >
                      ✕
                    </IconBtn>
                  </div>
                </div>
                {isVisible && !isReadOnly && (
                  <div style={{ paddingLeft: 26 }}>
                    {el.type === "heading" || el.type === "subheading" || el.type === "button" ? (
                      <>
                        <TinyInput
                          value={typeof val === "string" ? val : val.value}
                          onChange={(e) => handleElementChange(el, e.target.value)}
                        />
                        {el.type === "button" && el.contentKey === "cta_text" && (
                          <TinyInput
                            placeholder="Button URL (e.g. #donate)"
                            value={(content.cta_url as string) ?? ""}
                            onChange={(e) => updateSection(section.id, { ...content, cta_url: e.target.value })}
                            style={{ marginTop: 6 }}
                          />
                        )}
                        {(el.type === "heading" || el.type === "subheading") && el.arrayKey === "blocks" && el.arrayIndex !== undefined && (() => {
                          const levelOptions = el.type === "heading" ? [1, 2] : [3, 4, 5]
                          const defaultLevel = el.type === "heading" ? 1 : 3
                          const blocks = (content.blocks as Array<{ content: Record<string, unknown> }> | undefined) ?? []
                          const currentLevel = (blocks[el.arrayIndex]?.content?.level as number | undefined) ?? defaultLevel
                          const updateLevel = (lvl: number) => {
                            const arr = [...((content.blocks as unknown[]) ?? [])] as Record<string, unknown>[]
                            const blk = arr[el.arrayIndex!] as Record<string, unknown>
                            arr[el.arrayIndex!] = { ...blk, content: { ...(blk.content as Record<string, unknown>), level: lvl } }
                            updateSection(section.id, { ...content, blocks: arr })
                          }
                          return (
                            <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                              {levelOptions.map((lvl) => (
                                <button
                                  key={lvl}
                                  type="button"
                                  onClick={() => updateLevel(lvl)}
                                  style={{
                                    flex: 1,
                                    padding: "3px 0",
                                    borderRadius: 6,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    border: currentLevel === lvl ? "none" : "1px solid rgba(255,255,255,0.08)",
                                    background: currentLevel === lvl ? primaryColor : "rgba(255,255,255,0.04)",
                                    color: currentLevel === lvl ? "#fff" : "rgba(255,255,255,0.4)",
                                  }}
                                >
                                  H{lvl}
                                </button>
                              ))}
                            </div>
                          )
                        })()}
                      </>
                    ) : el.type === "paragraph" ? (
                      <RichTextInput
                        value={typeof val === "string" ? val : val.value}
                        onChange={(html) => handleElementChange(el, html)}
                        placeholder="Enter paragraph text…"
                      />
                    ) : el.type === "image" ? (
                      <div>
                        <div
                          style={{
                            height: 40,
                            borderRadius: 8,
                            border: "1px dashed rgba(255,255,255,0.08)",
                            fontSize: 10,
                            color: "rgba(255,255,255,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 8,
                            ...((content.image_url as string)
                              ? { background: `url(${content.image_url}) center/cover`, color: "transparent" }
                              : {}),
                          }}
                        >
                          Upload zone
                        </div>
                        {/* NOS-159: pick a stock photo for this image element */}
                        <button
                          type="button"
                          data-testid="stock-element-btn"
                          onClick={() =>
                            openStockPicker((url, alt) =>
                              updateSection(section.id, {
                                ...content,
                                image_url: url,
                                image_alt: (content.image_alt as string) || alt,
                              })
                            )
                          }
                          style={{
                            marginBottom: 8,
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            color: "rgba(255,255,255,0.4)",
                            fontSize: 11,
                            cursor: "pointer",
                            textDecoration: "underline",
                            textUnderlineOffset: 3,
                          }}
                        >
                          Browse stock photos
                        </button>
                        <TinyInput
                          placeholder="Alt text"
                          value={(content.image_alt as string) ?? ""}
                          onChange={(e) =>
                            updateSection(section.id, {
                              ...content,
                              image_alt: e.target.value,
                            })
                          }
                        />
                      </div>
                    ) : el.type === "card" ? (
                      (() => {
                        const cardIdx = el.arrayIndex!
                        const rawItem = ((content.items as Array<Record<string, unknown>>) ?? [])[cardIdx] ?? {}
                        const cardImgUrl = rawItem.image_url as string | undefined
                        const isCardUploading = uploadingCardIndex === cardIdx
                        const updateCardField = (field: string, value: unknown) => {
                          const arr = [...((content.items as unknown[]) ?? [])] as Record<string, unknown>[]
                          arr[cardIdx] = { ...arr[cardIdx], [field]: value }
                          updateSection(section.id, { ...content, items: arr })
                        }
                        const handleCardImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                          const file = e.target.files?.[0]
                          if (!file || !org?.id) return
                          if (file.size > IMAGE_MAX_BYTES) { toast.error("Image must be 2MB or smaller"); return }
                          const allowed = IMAGE_ACCEPT.split(",").map((m) => m.trim())
                          if (!allowed.some((m) => file.type === m)) { toast.error("Image must be PNG, JPEG, or SVG"); return }
                          setUploadingCardIndex(cardIdx)
                          const { data: uploaded, error: uploadError } = await uploadVersionedOrgImage(
                            supabase,
                            "logos",
                            `${org.id}/section-cards/${section.id}/${cardIdx}`,
                            file
                          )
                          if (uploadError || !uploaded) { setUploadingCardIndex(null); toast.error(uploadError?.message ?? "Upload failed"); return }
                          const arr = [...((content.items as unknown[]) ?? [])] as Record<string, unknown>[]
                          arr[cardIdx] = { ...arr[cardIdx], image_url: uploaded.publicUrl }
                          await updateSection(section.id, { ...content, items: arr })
                          setUploadingCardIndex(null)
                          toast.success("Image uploaded")
                          e.target.value = ""
                        }
                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <TinyInput
                              placeholder="Title"
                              value={(rawItem.title as string) ?? ""}
                              onChange={(e) => updateCardField("title", e.target.value)}
                            />
                            <RichTextInput
                              value={(rawItem.body as string) ?? ""}
                              onChange={(html) => updateCardField("body", html)}
                              placeholder="Body copy…"
                            />
                            {/* Card image upload zone */}
                            <label style={{ display: "block", cursor: isCardUploading ? "default" : "pointer" }}>
                              <input
                                type="file"
                                accept={IMAGE_ACCEPT}
                                style={{ display: "none" }}
                                disabled={isCardUploading}
                                onChange={(e) => { void handleCardImgUpload(e) }}
                              />
                              <div
                                style={{
                                  minHeight: 48,
                                  borderRadius: 8,
                                  border: "1px dashed rgba(255,255,255,0.1)",
                                  background: cardImgUrl
                                    ? `url(${cardImgUrl}) center/cover`
                                    : "rgba(255,255,255,0.02)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 6,
                                  color: "rgba(255,255,255,0.35)",
                                  fontSize: 11,
                                  transition: "all 0.12s",
                                }}
                              >
                                {isCardUploading ? (
                                  <span>Uploading…</span>
                                ) : cardImgUrl ? (
                                  <span style={{ background: "rgba(0,0,0,0.5)", padding: "2px 8px", borderRadius: 4 }}>Change image</span>
                                ) : (
                                  <span>+ Upload image</span>
                                )}
                              </div>
                            </label>
                            {cardImgUrl && (
                              <button
                                type="button"
                                onClick={() => updateCardField("image_url", "")}
                                style={{
                                  alignSelf: "flex-start",
                                  padding: "2px 8px",
                                  background: "rgba(239,68,68,0.15)",
                                  border: "1px solid rgba(239,68,68,0.25)",
                                  borderRadius: 6,
                                  color: "#ef4444",
                                  fontSize: 10,
                                  cursor: "pointer",
                                }}
                              >
                                Remove image
                              </button>
                            )}
                            <div style={{ display: "flex", gap: 6 }}>
                              <TinyInput
                                placeholder="CTA text"
                                value={(rawItem.cta_text as string) ?? ""}
                                onChange={(e) => updateCardField("cta_text", e.target.value)}
                              />
                              <TinyInput
                                placeholder="CTA URL"
                                value={(rawItem.cta_url as string) ?? ""}
                                onChange={(e) => updateCardField("cta_url", e.target.value)}
                              />
                            </div>
                          </div>
                        )
                      })()
                    ) : el.type === "stat" || el.type === "team-member" ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", gap: 8 }}>
                          <TinyInput
                            placeholder={el.type === "stat" ? "Value" : "Name"}
                            value={typeof val === "object" ? val.value : ""}
                            onChange={(e) =>
                              handleElementChange(el, {
                                value: e.target.value,
                                label: typeof val === "object" ? val.label : "",
                                icon: typeof val === "object" ? val.icon : undefined,
                                bio: typeof val === "object" ? val.bio : undefined,
                              })
                            }
                          />
                          <TinyInput
                            placeholder={el.type === "stat" ? "Label" : "Role"}
                            value={typeof val === "object" ? val.label : ""}
                            onChange={(e) =>
                              handleElementChange(el, {
                                value: typeof val === "object" ? val.value : "",
                                label: e.target.value,
                                icon: typeof val === "object" ? val.icon : undefined,
                                bio: typeof val === "object" ? val.bio : undefined,
                              })
                            }
                          />
                        </div>
                        {el.type === "stat" && (
                          <div>
                            <span
                              style={{
                                display: "block",
                                fontSize: 10,
                                fontWeight: 600,
                                color: "rgba(255,255,255,0.35)",
                                marginBottom: 4,
                              }}
                            >
                              Icon
                            </span>
                            <select
                              value={(typeof val === "object" ? val.icon : "") || ""}
                              onChange={(e) =>
                                handleElementChange(el, {
                                  value: typeof val === "object" ? val.value : "",
                                  label: typeof val === "object" ? val.label : "",
                                  icon: e.target.value,
                                })
                              }
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 8,
                                color: "#fff",
                                fontSize: 11,
                                cursor: "pointer",
                              }}
                            >
                              {["dollar", "users", "calendar", "heart", "star", "check"].map((name) => (
                                <option key={name} value={name} style={{ background: "#0c0c10", color: "#fff" }}>
                                  {name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {el.type === "team-member" && (
                          <TinyInput
                            multiline
                            placeholder="Short bio (optional)"
                            value={(typeof val === "object" ? val.bio : undefined) ?? ""}
                            onChange={(e) =>
                              handleElementChange(el, {
                                value: typeof val === "object" ? val.value : "",
                                label: typeof val === "object" ? val.label : "",
                                bio: e.target.value,
                              })
                            }
                          />
                        )}
                      </div>
                    ) : (
                      <span
                        style={{
                          fontSize: 11,
                          color: "rgba(255,255,255,0.25)",
                        }}
                      >
                        {el.type} — configure in properties
                      </span>
                    )}
                  </div>
                )}
                {isReadOnly && (
                  <div style={{ paddingLeft: 26, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                    {typeof val === "string" ? val : "Read-only"}
                  </div>
                )}
                {!isReadOnly && (
                  <div style={{ paddingLeft: 26, marginTop: 4 }}>
                    <button
                      type="button"
                      onClick={() => setOpenElStyles((prev) => ({ ...prev, [el.id]: !prev[el.id] }))}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "3px 0",
                        background: "none",
                        border: "none",
                        color: "rgba(255,255,255,0.25)",
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: "uppercase" as const,
                        letterSpacing: "0.1em",
                        cursor: "pointer",
                      }}
                    >
                      Style
                      <span style={{ fontSize: 11 }}>{openElStyles[el.id] ? "−" : "+"}</span>
                    </button>
                    {openElStyles[el.id] && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                        {(() => {
                          const elSt = getElStyle(el)
                          const caps = ELEMENT_STYLE_CAPS[el.type] ?? {}
                          return (
                            <>
                              {caps.text_align && (
                                <div style={{ display: "flex", gap: 4 }}>
                                  {(["left", "center", "right"] as const).map((align) => (
                                    <button
                                      key={align}
                                      type="button"
                                      onClick={() =>
                                        updateElStyle(el, {
                                          text_align: elSt.text_align === align ? undefined : align,
                                        })
                                      }
                                      style={{
                                        flex: 1,
                                        padding: "4px 0",
                                        borderRadius: 6,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        textTransform: "capitalize" as const,
                                        cursor: "pointer",
                                        border:
                                          elSt.text_align === align
                                            ? "none"
                                            : "1px solid rgba(255,255,255,0.08)",
                                        background:
                                          elSt.text_align === align
                                            ? primaryColor
                                            : "rgba(255,255,255,0.04)",
                                        color:
                                          elSt.text_align === align ? "#fff" : "rgba(255,255,255,0.4)",
                                      }}
                                    >
                                      {align}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {caps.background && (
                                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <label
                                    title={elSt.background ? elSt.background : "No override — using default"}
                                    style={{
                                      position: "relative",
                                      width: 28,
                                      height: 28,
                                      borderRadius: 6,
                                      border: "1px solid rgba(255,255,255,0.15)",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                      overflow: "hidden",
                                      background: elSt.background && elSt.background !== "transparent"
                                        ? elSt.background
                                        : "repeating-conic-gradient(rgba(255,255,255,0.12) 0% 25%, rgba(255,255,255,0.04) 0% 50%) 0 0 / 8px 8px",
                                    }}
                                  >
                                    <input
                                      type="color"
                                      value={
                                        elSt.background && elSt.background !== "transparent"
                                          ? elSt.background
                                          : "#ffffff"
                                      }
                                      onChange={(e) => updateElStyle(el, { background: e.target.value })}
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        opacity: 0,
                                        width: "100%",
                                        height: "100%",
                                        cursor: "pointer",
                                        padding: 0,
                                        border: "none",
                                      }}
                                    />
                                  </label>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: "rgba(255,255,255,0.4)",
                                    }}
                                  >
                                    BG
                                  </span>
                                  {elSt.background && (
                                    <button
                                      type="button"
                                      onClick={() => updateElStyle(el, { background: undefined })}
                                      style={{
                                        padding: "2px 6px",
                                        fontSize: 10,
                                        background: "rgba(255,255,255,0.06)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        borderRadius: 6,
                                        color: "rgba(255,255,255,0.7)",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              )}
                              {caps.text_color && (
                                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <label
                                    title={elSt.text_color ? elSt.text_color : "No override — using default"}
                                    style={{
                                      position: "relative",
                                      width: 28,
                                      height: 28,
                                      borderRadius: 6,
                                      border: "1px solid rgba(255,255,255,0.15)",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                      overflow: "hidden",
                                      background: elSt.text_color && elSt.text_color !== "transparent"
                                        ? elSt.text_color
                                        : "repeating-conic-gradient(rgba(255,255,255,0.12) 0% 25%, rgba(255,255,255,0.04) 0% 50%) 0 0 / 8px 8px",
                                    }}
                                  >
                                    <input
                                      type="color"
                                      value={
                                        elSt.text_color && elSt.text_color !== "transparent"
                                          ? elSt.text_color
                                          : "#000000"
                                      }
                                      onChange={(e) => updateElStyle(el, { text_color: e.target.value })}
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        opacity: 0,
                                        width: "100%",
                                        height: "100%",
                                        cursor: "pointer",
                                        padding: 0,
                                        border: "none",
                                      }}
                                    />
                                  </label>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: "rgba(255,255,255,0.4)",
                                    }}
                                  >
                                    Text
                                  </span>
                                  {elSt.text_color && (
                                    <button
                                      type="button"
                                      onClick={() => updateElStyle(el, { text_color: undefined })}
                                      style={{
                                        padding: "2px 6px",
                                        fontSize: 10,
                                        background: "rgba(255,255,255,0.06)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        borderRadius: 6,
                                        color: "rgba(255,255,255,0.7)",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              )}
                              {caps.corner_radius && (
                                <Select
                                  value={elSt.corner_radius ?? "default"}
                                  onValueChange={(v) =>
                                    updateElStyle(el, {
                                      corner_radius:
                                        v === "default"
                                          ? undefined
                                          : (v as BlockStyleOverrides["corner_radius"]),
                                    })
                                  }
                                >
                                  <SelectTrigger
                                    className="h-7 border-white/10 bg-white/5 text-xs text-white"
                                   
                                  >
                                    <SelectValue placeholder="Corner radius" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[1100] bg-[#0c0c10] border-white/10 text-white">
                                    {(
                                      [
                                        ["default", "Corner radius"],
                                        ["none", "Square"],
                                        ["sm", "Small"],
                                        ["md", "Medium"],
                                        ["lg", "Large"],
                                        ["full", "Full"],
                                      ] as const
                                    ).map(([v, label]) => (
                                      <SelectItem
                                        key={v}
                                        value={v}
                                        className="focus:bg-white/10 focus:text-white"
                                       
                                      >
                                        {label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {caps.padding && (
                                <Select
                                  value={elSt.padding ?? "default"}
                                  onValueChange={(v) =>
                                    updateElStyle(el, {
                                      padding:
                                        v === "default"
                                          ? undefined
                                          : (v as BlockStyleOverrides["padding"]),
                                    })
                                  }
                                >
                                  <SelectTrigger
                                    className="h-7 border-white/10 bg-white/5 text-xs text-white"
                                   
                                  >
                                    <SelectValue placeholder="Padding" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[1100] bg-[#0c0c10] border-white/10 text-white">
                                    {(
                                      [
                                        ["default", "Padding"],
                                        ["compact", "Compact"],
                                        ["normal", "Normal"],
                                        ["spacious", "Spacious"],
                                      ] as const
                                    ).map(([v, label]) => (
                                      <SelectItem
                                        key={v}
                                        value={v}
                                        className="focus:bg-white/10 focus:text-white"
                                       
                                      >
                                        {label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              <div style={{ marginTop: caps.corner_radius || caps.padding ? 4 : 0 }}>
                                <input
                                  type="text"
                                  placeholder="CSS class"
                                  value={getElStyle(el).custom_class ?? ""}
                                  onChange={(e) => updateElStyle(el, { custom_class: e.target.value || undefined })}
                                  style={{
                                    width: "100%",
                                    padding: "4px 8px",
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    borderRadius: 6,
                                    color: "rgba(255,255,255,0.7)",
                                    fontSize: 10,
                                    outline: "none",
                                    boxSizing: "border-box" as const,
                                  }}
                                />
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* + Add element - Section 9, NOS-91 */}
          {showAddElementCatalog ? (
            <AddElementCatalog
              primaryColor={primaryColor}
              onSelect={handleAddElement}
              onCancel={() => setShowAddElementCatalog(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowAddElementCatalog(true)}
              style={{
                padding: 8,
                background: "rgba(255,255,255,0.02)",
                border: "1px dashed rgba(255,255,255,0.1)",
                borderRadius: 8,
                color: "rgba(255,255,255,0.35)",
                fontSize: 11,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 14 }}>＋</span>
              Add element
            </button>
          )}

          {/* Footer: background color (NOS-217) — imported from the source site or set here */}
          {section.section_type === "footer" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase" as const, letterSpacing: "0.1em" }}>
                Footer Background
              </label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label
                  title={footerBackground || "Default dark (#0f0f14)"}
                  style={{
                    position: "relative",
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid rgba(255,255,255,0.15)",
                    cursor: "pointer",
                    flexShrink: 0,
                    overflow: "hidden",
                    background: footerBackgroundOrDefault,
                  }}
                >
                  <input
                    type="color"
                    value={footerBackgroundOrDefault}
                    onChange={(e) => handleFooterBackgroundChange(e.target.value)}
                    style={{
                      position: "absolute",
                      inset: 0,
                      opacity: 0,
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      padding: 0,
                      border: "none",
                    }}
                  />
                </label>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                  {footerBackground || "Default dark"}
                </span>
                {footerBackground && (
                  <button
                    type="button"
                    onClick={handleClearFooterBackground}
                    style={{
                      padding: "2px 6px",
                      fontSize: 10,
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 6,
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                    }}
                  >
                    Reset
                  </button>
                )}
              </div>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>
                Text and link colors adjust automatically for contrast.
              </p>
            </div>
          )}

          {/* NOS-234: per-group column assignment (brand stays in column 1) */}
          {section.section_type === "footer" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase" as const, letterSpacing: "0.1em" }}>
                Footer Layout
              </label>
              {([
                { group: "quick_links", label: "Quick links" },
                { group: "connect", label: "Social links" },
                { group: "contact", label: "Contact & address" },
                // NOS-232: imported newsletter block, only when present
                ...(content.subscribe != null
                  ? [{ group: "subscribe", label: "Newsletter signup" } as const]
                  : []),
              ] as const).map(({ group, label }) => (
                <StyleSegmentRow
                  key={group}
                  label={label}
                  options={[
                    { id: "1", label: "Col 1" },
                    { id: "2", label: "Col 2" },
                    { id: "3", label: "Col 3" },
                  ]}
                  activeId={String(footerColumnLayout[group])}
                  primaryColor={primaryColor}
                  onSelect={(id) => handleFooterColumnChange(group, Number(id) as 1 | 2 | 3)}
                />
              ))}
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>
                Your logo and tagline stay in column 1.
              </p>
            </div>
          )}

          {/* Footer: dedicated logo (light variant for the dark background) */}
          {section.section_type === "footer" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase" as const, letterSpacing: "0.1em" }}>
                Footer Logo
              </label>
              {footerLogoUrl ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 10,
                    background: footerBackgroundOrDefault,
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8,
                  }}
                >
                  <img
                    src={footerLogoUrl}
                    alt="Footer logo"
                    style={{ height: 32, width: "auto", maxWidth: 160, objectFit: "contain" }}
                  />
                  <IconBtn size={22} danger onClick={handleRemoveFooterLogo} title="Remove footer logo">✕</IconBtn>
                </div>
              ) : (
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: 0 }}>
                  Using your main logo. The footer is dark — upload a light version if your logo is hard to see.
                </p>
              )}
              <label
                style={{
                  width: "100%", padding: 7, background: "rgba(255,255,255,0.02)",
                  border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 7,
                  color: "rgba(255,255,255,0.3)", fontSize: 10, cursor: uploadingFooterLogo ? "default" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span>＋</span>
                <span>{uploadingFooterLogo ? "Uploading…" : footerLogoUrl ? "Replace footer logo" : "Upload footer logo"}</span>
                <input
                  type="file"
                  accept={IMAGE_ACCEPT}
                  onChange={handleFooterLogoUpload}
                  disabled={uploadingFooterLogo}
                  style={{ display: "none" }}
                />
              </label>
              {!footerLogoUrl && (
                <>
                  <label style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase" as const, letterSpacing: "0.1em", marginTop: 4 }}>
                    Logo Style
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {([
                      { value: "knockout", label: "White" },
                      { value: "chip", label: "Original on chip" },
                    ] as const).map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleFooterLogoTreatment(opt.value)}
                        style={{
                          flex: 1,
                          padding: "6px 8px",
                          fontSize: 11,
                          borderRadius: 7,
                          cursor: "pointer",
                          background: footerLogoTreatment === opt.value ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.03)",
                          border: footerLogoTreatment === opt.value ? "1px solid rgba(255,255,255,0.3)" : "1px solid rgba(255,255,255,0.08)",
                          color: footerLogoTreatment === opt.value ? "#fff" : "rgba(255,255,255,0.5)",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>
                    Applies when your logo would blend into the footer background. White renders it as a clean silhouette; chip keeps the full-color logo on a contrasting card. Uploaded footer logos are always shown as-is.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Footer: Quick Links management — NavContextPanel style */}
          {section.section_type === "footer" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase" as const, letterSpacing: "0.1em" }}>
                Quick Links
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {qlLocalItems.map((link, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={(e) => handleQlDragStart(e, i)}
                    onDragEnter={(e) => handleQlDragEnter(e, i)}
                    onDragOver={handleQlDragOver}
                    onDrop={(e) => handleQlDrop(e, i)}
                    onDragEnd={handleQlDragEnd}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      background: qlDropTargetIndex === i && qlDraggingIndex !== i ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${qlDropTargetIndex === i && qlDraggingIndex !== i ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)"}`,
                      borderRadius: 8,
                      opacity: qlDraggingIndex === i ? 0.4 : 1,
                      transition: "background 0.1s, border-color 0.1s, opacity 0.1s",
                    }}
                  >
                    <span style={{ cursor: "grab", color: "rgba(255,255,255,0.25)", fontSize: 12, lineHeight: 1, flexShrink: 0, userSelect: "none", paddingRight: 2 }} title="Drag to reorder">⠿</span>
                    <TinyInput
                      value={qlLabelDrafts[i] ?? link.label}
                      onChange={(e) => setQlLabelDrafts((p) => ({ ...p, [i]: e.target.value }))}
                      onBlur={() => handleQlLabelBlur(i)}
                      placeholder="Label"
                      style={{ flex: 1 }}
                    />
                    <TinyInput
                      value={qlHrefDrafts[i] ?? link.href}
                      onChange={(e) => setQlHrefDrafts((p) => ({ ...p, [i]: e.target.value }))}
                      onBlur={() => handleQlHrefBlur(i)}
                      placeholder="/about or https://…"
                      style={{ flex: 1, fontSize: 11 }}
                    />
                    <button
                      type="button"
                      title={link.open_in_new_tab ? "Opens in new tab • Click to open in same tab" : "Opens in same tab • Click to open in new tab"}
                      onClick={() => handleQlToggleNewTab(i)}
                      style={{
                        width: 22, height: 22, minWidth: 22, minHeight: 22, padding: 0, cursor: "pointer",
                        border: `1px solid ${link.open_in_new_tab ? primaryColor : "rgba(255,255,255,0.4)"}`,
                        background: link.open_in_new_tab ? primaryColor : "transparent",
                        borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, color: link.open_in_new_tab ? "#fff" : "rgba(255,255,255,0.5)",
                        fontSize: 12, lineHeight: 1, transition: "all 0.12s",
                      }}
                    >↗</button>
                    <IconBtn size={22} danger onClick={() => handleRemoveQuickLink(i)}>✕</IconBtn>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setAddingQuickLink(!addingQuickLink)}
                style={{
                  width: "100%", padding: 7, background: "rgba(255,255,255,0.02)",
                  border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 7,
                  color: "rgba(255,255,255,0.3)", fontSize: 10, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span>＋</span><span>Add link</span>
              </button>
              {addingQuickLink && (
                <div style={{ padding: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  <TinyInput placeholder="Label (e.g. About)" value={newQLLabel} onChange={(e) => setNewQLLabel(e.target.value)} />
                  <TinyInput placeholder="Href (e.g. /about or https://…)" value={newQLHref} onChange={(e) => setNewQLHref(e.target.value)} />
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "rgba(255,255,255,0.5)", cursor: "pointer" }}>
                    <input type="checkbox" checked={newQLNewTab} onChange={(e) => setNewQLNewTab(e.target.checked)} style={{ width: 14, height: 14 }} />
                    Open in new tab
                  </label>
                  <button
                    type="button"
                    onClick={handleAddQuickLink}
                    disabled={!newQLLabel.trim() || !newQLHref.trim()}
                    style={{
                      padding: "6px 12px", background: primaryColor, border: "none", borderRadius: 6,
                      color: "#fff", fontSize: 11, cursor: !newQLLabel.trim() || !newQLHref.trim() ? "default" : "pointer",
                      opacity: !newQLLabel.trim() || !newQLHref.trim() ? 0.6 : 1,
                    }}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Footer: Social Links management */}
          {section.section_type === "footer" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Social Links
              </div>
              {SOCIAL_PLATFORMS.map((platform) => (
                <div key={platform} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      fontSize: 10,
                      color: "rgba(255,255,255,0.45)",
                      width: 72,
                      flexShrink: 0,
                    }}
                  >
                    {platform}
                  </span>
                  <TinyInput
                    placeholder={`${platform} URL`}
                    value={getSocialUrl(platform)}
                    onChange={(e) => setSocialUrl(platform, e.target.value)}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Section custom class — inline (Outline view) only */}
          {inline && (
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 12 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.25)", textTransform: "uppercase" as const, letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>
                CSS Class
              </label>
              <input
                type="text"
                placeholder="e.g. featured-section"
                value={(content._section_class as string | undefined) ?? ""}
                onChange={(e) => updateSection(section.id, { ...content, _section_class: e.target.value })}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 11,
                  outline: "none",
                  boxSizing: "border-box" as const,
                }}
              />
            </div>
          )}

          {/* Rewrite with AI - NOS-117 */}
          <div>
            <TinyInput
              placeholder="e.g., make it shorter, use more formal tone"
              value={rewriteInstruction}
              onChange={(e) => setRewriteInstruction(e.target.value)}
              disabled={isRewriting}
              style={{ marginBottom: 8 }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                type="button"
                onClick={() => void handleRewriteAI()}
                disabled={isRewriting}
                style={{
                  padding: "9px 14px",
                  background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}08)`,
                  border: `1px solid ${primaryColor}30`,
                  borderRadius: 9,
                  color: primaryColor,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: isRewriting ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  opacity: isRewriting ? 0.7 : 1,
                }}
              >
                {isRewriting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Rewriting…
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 13 }}>✦</span>
                    Rewrite section with AI
                  </>
                )}
              </button>
              {canUndo && (
                <button
                  type="button"
                  onClick={undo}
                  style={{
                    padding: "6px 12px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8,
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 10,
                    cursor: "pointer",
                  }}
                >
                  Undo rewrite
                </button>
              )}
            </div>
          </div>

          {/* Remove section - NOS-93. Spec Section 8, Rule #5: hidden for Hero/Footer */}
          {!hideRemove && (
            <>
              <button
                type="button"
                data-testid="section-remove-open"
                onClick={() => setRemoveDialogOpen(true)}
                style={{
                  padding: 8,
                  background: "rgba(239,68,68,0.06)",
                  border: "1px solid rgba(239,68,68,0.12)",
                  borderRadius: 8,
                  color: "rgba(239,68,68,0.6)",
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Remove section
              </button>
              <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Remove {sectionLabel} section?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this section and all its
                      content. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="section-remove-confirm"
                      onClick={() => void handleRemoveSection()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
    </>
  )
}
